import { getHitsContext, getAutoSessionId, HitsContext } from './hits-context';

export interface HitConfig {
  merchantId: string;
  host: string;
}

export interface HitOptions {
  useBeacon?: boolean;
  extraContext?: HitsContext;
}

export interface HitPayload {
  name: string;
  eventType: string;
  data?: Record<string, unknown>;
}

const HITS_PATH = '/api/v1/events';

function buildEnvelope(
  hit: HitPayload,
  cfg: HitConfig,
  options: HitOptions = {},
): Record<string, unknown> {
  const sticky = getHitsContext();
  const extra = options.extraContext ?? {};
  return {
    // sessionId fallback first so caller-supplied sticky/extra sessionId wins
    sessionId: getAutoSessionId(),
    ...sticky,
    ...extra,
    // reserved fields last — always win over context
    name: hit.name,
    eventType: hit.eventType,
    timestamp: Date.now(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    version: '1',
    type: 'event',
    merchantId: cfg.merchantId,
    // always emit data (matches existing hits client parity: `data: {}` when caller omits)
    data: hit.data ?? {},
  };
}

function postWithFetch(url: string, body: string): void {
  if (typeof fetch !== 'function') return;
  // No credentials: hits collector responds with Access-Control-Allow-Origin: *,
  // which CORS forbids combining with credentials. Matches existing hits client.
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

export function sendHit(hit: HitPayload, cfg: HitConfig, options: HitOptions = {}): void {
  try {
    const envelope = buildEnvelope(hit, cfg, options);
    const body = JSON.stringify(envelope);
    const url = `${cfg.host}${HITS_PATH}`;
    if (options.useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      } catch {
        /* fall through to fetch */
      }
    }
    postWithFetch(url, body);
  } catch {
    /* fire-and-forget: never let analytics break the page */
  }
}
